import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { adminMetricsController, adminVehicleController } from '@/api/generated/endpoints';
import { unwrap } from '@/api/http';
import { useMarket } from '@/app/market';
import { useAuth } from '@/auth/AuthProvider';
import { formatNumber } from '@/lib/format';
import { useMarketCatalog } from '@/lib/markets';
import { LiveDot, Popover, PopoverContent, PopoverTrigger, cn, focusRing } from '@/ui';
import { GoalDetails } from './goal/GoalBar';
import { useRideGoal } from './goal/RideGoalProvider';

/** The SL2 metrics payload fields the strip reads (GET /api/admins/metrics/sl2). */
interface Sl2Counts {
  rides: number;
  swaps: number;
  /** riders on the road now (the legacy panel's number, without a ride count query) */
  active_rides?: number;
}
/** Active rides per minute over the last ~30 minutes, per market id and 'total'. */
interface Sl2RidesTs {
  generated_utc?: string;
  today?: { markets?: Record<string, { r?: number[] }> };
}
interface Sl2Metrics {
  generated?: string;
  totals: Sl2Counts;
  markets: (Sl2Counts & { name: string; area_id?: number })[];
  rides_ts?: Sl2RidesTs;
}

/** SL2 counts for the working market (by id, else by name), or the all-markets totals. */
export function sl2For(metrics: Sl2Metrics | null | undefined, marketName: string | undefined, areaId?: number): Sl2Counts | null {
  if (!metrics) return null;
  if (marketName === undefined && areaId === undefined) return metrics.totals;
  const byId = areaId === undefined ? undefined : metrics.markets.find((m) => m.area_id === areaId);
  if (byId) return byId;
  const wanted = marketName?.trim().toLowerCase();
  return (wanted && metrics.markets.find((m) => m.name.trim().toLowerCase() === wanted)) || null;
}

/** SL2's per-minute active rides for the market (or all markets), newest last; null when absent. */
export function sl2Series(metrics: Sl2Metrics | null | undefined, areaId: number | undefined): Sample[] | null {
  const ts = metrics?.rides_ts;
  const r = ts?.today?.markets?.[areaId === undefined ? 'total' : String(areaId)]?.r;
  if (!r || r.length < 2 || r.every((v) => v === 0)) return null;
  const end = Date.parse(ts?.generated_utc ?? metrics?.generated ?? '');
  const last = Number.isNaN(end) ? Date.now() : end;
  return r.map((value, i) => ({ at: last - (r.length - 1 - i) * 60_000, value }));
}

const POLL_MS = 60_000;
const WINDOW_MS = 30 * 60_000;
const TREND_MS = 10 * 60_000;

interface Sample {
  at: number;
  value: number;
}

/** Active-ride counts seen in this tab for the last 30 minutes, per market (survives reloads). */
function useActiveHistory(key: string, value: number | undefined): Sample[] {
  const storageKey = `manhattan.live.${key}`;
  const [samples, setSamples] = useState<Sample[]>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]') as Sample[];
      return saved.filter((s) => Date.now() - s.at < WINDOW_MS);
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (value == null) return;
    setSamples((prev) => {
      const now = Date.now();
      const next = [...prev.filter((s) => now - s.at < WINDOW_MS), { at: now, value }];
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* private mode: keep it in memory */
      }
      return next;
    });
  }, [value, storageKey]);
  return samples;
}

/** Change against the sample closest to ten minutes ago, once there is one that old. */
export function trendOf(samples: Sample[], now = Date.now()): number | null {
  const last = samples.at(-1);
  if (!last) return null;
  const old = samples.filter((s) => now - s.at >= TREND_MS).at(-1);
  return old ? last.value - old.value : null;
}

/**
 * Live ridership (the legacy "dynamic panel"), in the header on every page: riders on the road
 * now with a 30-minute trend, plus online vehicles, today's rides and swaps for the working
 * market. Opens a detail panel. Shown with dynamic_panel:read.
 */
export function LiveRidership() {
  const { t } = useTranslation('shell');
  const { permissions } = useAuth();
  const { areaId } = useMarket();
  const markets = useMarketCatalog();
  const allowed = permissions.canView('dynamic_panel');
  const areaIds = areaId ? [areaId] : undefined;

  const online = useQuery({
    queryKey: ['live', 'online-vehicles', areaId],
    queryFn: async () => unwrap(await adminVehicleController.countSearch({ search: { connected: true, areaIds } })),
    enabled: allowed,
    refetchInterval: 120_000,
  });
  const sl2 = useQuery({
    queryKey: ['live', 'sl2'],
    queryFn: async () => unwrap(await adminMetricsController.getSl2Metrics()) as Sl2Metrics,
    enabled: allowed,
    refetchInterval: POLL_MS,
  });
  const marketName = areaId ? markets.names.get(areaId) : undefined;
  const today = sl2For(sl2.data, marketName, areaId ?? undefined);
  const activeRides = today?.active_rides == null ? null : Math.round(today.active_rides);
  const sampled = useActiveHistory(String(areaId ?? 'all'), activeRides ?? undefined);
  const history = sl2Series(sl2.data, areaId ?? undefined) ?? sampled;

  if (!allowed) return null;
  const trend = trendOf(history);
  const stats = [
    { key: 'online', label: t('live.online'), value: online.data, show: 'xl:flex' },
    { key: 'rides', label: t('live.ridesToday'), value: today?.rides, show: 'lg:flex' },
    { key: 'swaps', label: t('live.swaps'), value: today?.swaps, show: '2xl:flex' },
  ];
  const countLabel = activeRides == null ? undefined : t('live.activeRidesCount', { count: activeRides });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('live.open', { what: countLabel ?? t('live.title') })}
          className={cn(
            'm-0 flex h-10 shrink-0 cursor-pointer items-center gap-3 rounded-lg border border-line bg-subtle px-3 text-left font-sans text-ink hover:border-accent hover:bg-accent-softer',
            focusRing,
          )}
        >
          <span className="flex items-center gap-2">
            <LiveDot />
            <Odometer value={activeRides} className="font-display text-xl font-bold leading-none text-heading" />
            <span className="hidden flex-col leading-tight md:flex">
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink-muted">{t('live.ridingNow')}</span>
              <Trend delta={trend} compact />
            </span>
          </span>
          <Sparkline samples={history} className="hidden h-6 w-16 lg:block" />
          {stats.map((s) => (
            <span key={s.key} className={cn('hidden flex-col border-l border-line pl-3 leading-tight', s.show)}>
              <span className="text-sm font-bold tabular-nums text-ink">{s.value == null ? '–' : formatNumber(Math.round(s.value))}</span>
              <span className="text-[11px] text-ink-muted">{s.label}</span>
            </span>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] max-w-[calc(100vw-1.5rem)] p-0" aria-label={t('live.title')}>
        <LiveDetails
          marketName={marketName}
          active={activeRides ?? undefined}
          trend={trend}
          history={history}
          stats={stats}
          byMarket={areaId ? undefined : sl2.data?.markets}
        />
      </PopoverContent>
    </Popover>
  );
}

function LiveDetails({
  marketName,
  active,
  trend,
  history,
  stats,
  byMarket,
}: {
  marketName: string | undefined;
  active: number | undefined;
  trend: number | null;
  history: Sample[];
  stats: { key: string; label: string; value: number | undefined | null }[];
  byMarket: (Sl2Counts & { name: string })[] | undefined;
}) {
  const { t } = useTranslation('shell');
  const goal = useRideGoal();
  const top = useMemo(() => [...(byMarket ?? [])].sort((a, b) => b.rides - a.rides).slice(0, 6), [byMarket]);
  const max = top[0]?.rides ?? 0;
  return (
    <section aria-label={t('live.title')}>
      <header className="flex items-center gap-2 border-b border-line px-4 py-3">
        <LiveDot />
        <span className="font-display text-sm font-semibold text-heading">{t('live.title')}</span>
        <span className="ml-auto text-xs text-ink-muted">{marketName ?? t('live.allMarkets')}</span>
      </header>
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="font-display text-4xl font-bold leading-none tabular-nums text-heading">{active == null ? '–' : formatNumber(active)}</div>
            <div className="mt-1 text-sm text-ink-muted">{t('live.ridingNow')}</div>
          </div>
          <Trend delta={trend} />
        </div>
        <div>
          <Sparkline samples={history} className="h-16 w-full" filled />
          <div className="mt-1 flex justify-between text-[11px] text-ink-faint">
            <span>{t('live.thirtyMinAgo')}</span>
            <span>{t('live.now')}</span>
          </div>
        </div>
        <dl className="m-0 grid grid-cols-3 gap-2">
          {stats.map((s) => (
            <div key={s.key} className="rounded-md border border-line px-2.5 py-2">
              <dd className="m-0 text-base font-bold tabular-nums text-ink">{s.value == null ? '–' : formatNumber(Math.round(s.value))}</dd>
              <dt className="text-[11px] text-ink-muted">{s.label}</dt>
            </div>
          ))}
        </dl>
        {top.length > 0 && (
          <div>
            <h3 className="m-0 mb-2 text-xs font-bold uppercase tracking-wide text-ink-muted">{t('live.byMarket')}</h3>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {top.map((m) => (
                <li key={m.name} className="grid grid-cols-[5rem_1fr_auto] items-center gap-2 text-xs">
                  <span className="truncate text-ink">{m.name}</span>
                  <span className="h-1.5 overflow-hidden rounded-full bg-subtle">
                    <span className="block h-full rounded-full bg-brand" style={{ width: `${max ? (m.rides / max) * 100 : 0}%` }} />
                  </span>
                  <span className="tabular-nums text-ink-muted">{formatNumber(Math.round(m.rides))}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Link to="/operations/rides?progress=active" className={cn('inline-flex items-center gap-1 text-sm font-semibold text-accent no-underline hover:underline', focusRing)}>
          {t('live.seeRides')}
          <ArrowRight size={14} aria-hidden />
        </Link>
        <p className="m-0 text-[11px] text-ink-faint">{t('live.updates')}</p>
      </div>
      {/* the company goal: every market, whatever market is picked */}
      {goal && (
        <div className="border-t border-line">
          <GoalDetails goal={goal} limit={3} />
        </div>
      )}
    </section>
  );
}

/** "+12 vs 10 min ago": green up, red down, grey while there's no ten-minute-old sample. */
function Trend({ delta, compact }: { delta: number | null; compact?: boolean }) {
  const { t } = useTranslation('shell');
  if (delta == null) return compact ? null : <span className="text-xs text-ink-faint">{t('live.trendPending')}</span>;
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : ArrowRight;
  const tone = delta > 0 ? 'text-success-ink' : delta < 0 ? 'text-danger-ink' : 'text-ink-muted';
  const text = delta === 0 ? t('live.trendFlat') : t('live.trend', { delta: `${delta > 0 ? '+' : '−'}${formatNumber(Math.abs(delta))}` });
  return (
    <span className={cn('inline-flex items-center gap-0.5 whitespace-nowrap font-semibold tabular-nums', compact ? 'text-[11px]' : 'text-xs', tone)}>
      <Icon size={compact ? 12 : 14} aria-hidden />
      {compact ? `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${formatNumber(Math.abs(delta))}` : text}
    </span>
  );
}

/** The last 30 minutes of active rides as a line (and an area wash when `filled`). */
function Sparkline({ samples, className, filled }: { samples: Sample[]; className?: string; filled?: boolean }) {
  if (samples.length < 2) return <span aria-hidden className={cn('rounded bg-subtle/60', className)} />;
  const values = samples.map((s) => s.value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const t0 = samples[0]!.at;
  const tSpan = samples.at(-1)!.at - t0 || 1;
  const pts = samples.map((s) => [((s.at - t0) / tSpan) * 100, 28 - ((s.value - lo) / span) * 24] as const);
  const line = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const [lx, ly] = pts.at(-1)!;
  return (
    <svg aria-hidden viewBox="0 0 100 30" preserveAspectRatio="none" className={cn('overflow-visible', className)}>
      {filled && <polygon points={`0,30 ${line} 100,30`} className="fill-accent-soft" />}
      <polyline points={line} fill="none" vectorEffect="non-scaling-stroke" className="stroke-accent" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r={2.2} vectorEffect="non-scaling-stroke" className="fill-accent" />
    </svg>
  );
}

/** Digits that roll to their new value (instant under reduced motion). */
function Odometer({ value, className }: { value: number | null; className?: string }) {
  const text = value == null ? '–' : formatNumber(value);
  const previous = useRef(text);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (previous.current === text) return;
    previous.current = text;
    setBump(true);
    const id = window.setTimeout(() => setBump(false), 600);
    return () => window.clearTimeout(id);
  }, [text]);
  return (
    <span className={cn('inline-flex tabular-nums transition-colors duration-500', bump && 'text-accent', className)} aria-hidden>
      {text.split('').map((ch, i) =>
        /\d/.test(ch) ? (
          <span key={i} className="inline-block h-[1em] overflow-hidden">
            <span className="flex flex-col transition-transform duration-700 ease-out motion-reduce:transition-none" style={{ transform: `translateY(${-Number(ch)}em)` }}>
              {'0123456789'.split('').map((d) => (
                <span key={d} className="h-[1em] leading-none">
                  {d}
                </span>
              ))}
            </span>
          </span>
        ) : (
          <span key={i}>{ch}</span>
        ),
      )}
    </span>
  );
}
